export interface RelativeTimeOptions {
  /**
   * Formats the absolute date once the value is older than a week. Hosts pass a
   * zone-aware formatter (the Settings pages use the user's stored time zone)
   * so a page never shows two zones; the default is the browser's date format.
   */
  readonly formatAbsolute?: (ms: number) => string;
}

export function relativeTime(
  ts: string | number,
  options: RelativeTimeOptions = {},
): string {
  const ms = typeof ts === "number" ? ts : new Date(ts).getTime();
  /* Finite is not enough. `Date`'s representable range is +/-8.64e15 ms, so a
     finite-but-absurd value like -1e20 is a real number that every arithmetic
     branch below happily formats, and the `toLocaleDateString` fallback then
     renders the literal string "Invalid Date" into the notification feed. */
  if (!Number.isFinite(ms) || Math.abs(ms) > 8.64e15) return "";
  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  try {
    return options.formatAbsolute
      ? options.formatAbsolute(ms)
      : new Date(ms).toLocaleDateString();
  } catch {
    return `${d}d ago`;
  }
}
